import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Shield, Globe, CheckCircle2, Copy, ExternalLink,
  ArrowLeft, AlertTriangle, Info, FileText, Settings, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const PROVIDERS = [
  {
    id: 'resend',
    name: 'Resend',
    tagline: 'Modern, developer-friendly. Recommended for most users.',
    free_tier: '3,000 emails/month free, 100/day',
    paid_tier: 'Pro from $20/mo for 50,000 emails',
    signup_url: 'https://resend.com/signup',
    dashboard_url: 'https://resend.com/domains',
    docs_url: 'https://resend.com/docs/dashboard/domains/introduction',
    badge_color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    tagline: 'Industry standard, scales to millions. Owned by Twilio.',
    free_tier: '100 emails/day free forever',
    paid_tier: 'Essentials from $19.95/mo for 50,000 emails',
    signup_url: 'https://signup.sendgrid.com/',
    dashboard_url: 'https://app.sendgrid.com/settings/sender_auth/domains',
    docs_url: 'https://docs.sendgrid.com/ui/account-and-settings/how-to-set-up-domain-authentication',
    badge_color: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  },
  {
    id: 'zeptomail',
    name: 'ZeptoMail',
    tagline: 'Zoho\'s transactional service. Cheapest at high volume.',
    free_tier: '10,000 emails free (one-time), then pay-as-you-go',
    paid_tier: '$2.50 per 10,000 emails (cheaper than competitors)',
    signup_url: 'https://www.zoho.com/zeptomail/signup.html',
    dashboard_url: 'https://www.zoho.com/zeptomail/cpanel/mail-agents/',
    docs_url: 'https://www.zoho.com/zeptomail/help/domain-authentication.html',
    badge_color: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  },
];

function CopyableField({ label, value, name }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md p-3 mb-2" data-testid={`copy-field-${name}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <button onClick={copy} className="text-slate-400 hover:text-violet-300 text-xs flex items-center gap-1" data-testid={`copy-btn-${name}`}>
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
      <code className="block text-sm text-violet-300 font-mono break-all">{value}</code>
    </div>
  );
}

function Section({ icon: Icon, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-violet-400" />
          <h2 className="font-bold text-white text-base">{title}</h2>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}

export default function SuperAdminEmailSetupGuide() {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState('resend');
  const provider = PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <div className="space-y-6" data-testid="email-setup-guide-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate('/super-admin/email')} className="text-xs text-slate-400 hover:text-violet-300 flex items-center gap-1 mb-2" data-testid="back-link">
            <ArrowLeft className="h-3 w-3" /> Back to Email Notifications
          </button>
          <h1 className="text-2xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email Sender Setup Guide
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Configure DNS authentication (SPF, DKIM, DMARC) and connect a transactional email provider so Recruitizr's
            password resets, welcome emails, interview invites, and billing notices reach customers reliably.
          </p>
        </div>
      </div>

      {/* Why DNS auth */}
      <div className="bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-lg p-5" data-testid="why-dns-card">
        <div className="flex items-start gap-3">
          <Shield className="h-6 w-6 text-violet-400 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-bold text-white text-base mb-1">Why DNS authentication matters</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-3">
              Without SPF, DKIM, and DMARC records, Gmail and Outlook actively reject or quarantine your transactional emails as spam.
              Worse — Google's <strong>February 2024 rules</strong> require these for any sender pushing &gt;5,000 emails/day.
              Setup is one-time, takes about <strong>15 minutes</strong>, and dramatically improves deliverability.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <div className="bg-slate-900/60 rounded-md p-3 border border-slate-700">
                <p className="text-xs uppercase text-violet-300 font-bold tracking-wider mb-1">SPF</p>
                <p className="text-xs text-slate-300">Tells receivers WHICH servers are allowed to send mail "from" your domain.</p>
              </div>
              <div className="bg-slate-900/60 rounded-md p-3 border border-slate-700">
                <p className="text-xs uppercase text-violet-300 font-bold tracking-wider mb-1">DKIM</p>
                <p className="text-xs text-slate-300">Cryptographic signature proving the email wasn't tampered with in transit.</p>
              </div>
              <div className="bg-slate-900/60 rounded-md p-3 border border-slate-700">
                <p className="text-xs uppercase text-violet-300 font-bold tracking-wider mb-1">DMARC</p>
                <p className="text-xs text-slate-300">Policy that tells receivers what to do when SPF/DKIM fail (reject, quarantine, or report).</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Provider picker */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="provider-picker">
        <h2 className="font-bold text-white text-base mb-3 flex items-center gap-2">
          <Mail className="h-5 w-5 text-violet-400" /> Step 1 — Choose your email provider
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PROVIDERS.map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedProvider(p.id)}
              data-testid={`provider-${p.id}`}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                selectedProvider === p.id
                  ? 'border-violet-500 bg-violet-500/10'
                  : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-white">{p.name}</h3>
                {selectedProvider === p.id && <CheckCircle2 className="h-4 w-4 text-violet-400" />}
              </div>
              <p className="text-xs text-slate-400 mb-2">{p.tagline}</p>
              <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${p.badge_color}`}>
                {p.free_tier}
              </span>
              <p className="text-[11px] text-slate-500 mt-2">{p.paid_tier}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Sign up */}
      <Section icon={ExternalLink} title="Step 2 — Sign up & verify your sender domain" defaultOpen={true}>
        <ol className="space-y-3 text-sm text-slate-300">
          <li>
            <strong className="text-white">2.1</strong> Create a {provider.name} account:
            <a href={provider.signup_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-violet-300 hover:underline ml-1" data-testid={`signup-link-${provider.id}`}>
              {provider.signup_url} <ExternalLink className="h-3 w-3" />
            </a>
          </li>
          <li>
            <strong className="text-white">2.2</strong> In the {provider.name} dashboard, add the sender domain
            <code className="mx-1 px-2 py-0.5 bg-slate-800 text-violet-300 rounded text-xs">recruitizr.ai</code>
            (or your custom branded domain).
            <p className="text-xs text-slate-500 mt-1">
              Dashboard link: <a href={provider.dashboard_url} target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:underline">{provider.dashboard_url}</a>
            </p>
          </li>
          <li>
            <strong className="text-white">2.3</strong> {provider.name} will give you <strong>3 DNS records to add</strong>
            (typically one SPF TXT, two DKIM CNAMEs, and optionally a return-path/MX record).
          </li>
        </ol>

        <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">
            <strong>Don't copy DNS values from this guide</strong> — every provider generates unique DKIM keys per account.
            Copy them from your <strong>{provider.name}</strong> dashboard after Step 2.2.
          </p>
        </div>
      </Section>

      {/* Step 3: DNS records */}
      <Section icon={Globe} title="Step 3 — Add the DNS records to your domain registrar">
        <p className="text-sm text-slate-300 mb-3">
          Log into your DNS provider (Cloudflare, Namecheap, GoDaddy, Route 53, etc.) and add the records from your {provider.name} dashboard.
          Each record looks roughly like this:
        </p>

        <div className="space-y-4">
          <div className="bg-slate-800/40 rounded-md p-4 border border-slate-700">
            <p className="text-xs uppercase text-emerald-400 font-bold tracking-wider mb-2">SPF Record (TXT)</p>
            <CopyableField name="spf-host" label="Host / Name" value="@" />
            <CopyableField name="spf-type" label="Type" value="TXT" />
            {provider.id === 'resend' && (
              <CopyableField name="spf-resend" label="Value" value="v=spf1 include:_spf.resend.com ~all" />
            )}
            {provider.id === 'sendgrid' && (
              <CopyableField name="spf-sg" label="Value" value="v=spf1 include:sendgrid.net ~all" />
            )}
            {provider.id === 'zeptomail' && (
              <CopyableField name="spf-zm" label="Value" value="v=spf1 include:zeptomail.zoho.com ~all" />
            )}
            <p className="text-xs text-slate-500 mt-2">
              ☝️ If you already have an SPF record, <strong>merge</strong> — don't add a second one. Combine: <code className="bg-slate-900 px-1 rounded">v=spf1 include:_spf.google.com include:_spf.resend.com ~all</code>
            </p>
          </div>

          <div className="bg-slate-800/40 rounded-md p-4 border border-slate-700">
            <p className="text-xs uppercase text-sky-400 font-bold tracking-wider mb-2">DKIM Record (CNAME × 2 or TXT)</p>
            <p className="text-xs text-slate-400 mb-3">
              Each provider gives 1-2 unique DKIM keys. Add the exact <strong>Host</strong> and <strong>Value</strong> shown in your {provider.name} dashboard. Example structure:
            </p>
            <CopyableField name="dkim-host" label="Host (example)" value={`resend._domainkey.recruitizr.ai`} />
            <CopyableField name="dkim-type" label="Type" value="CNAME (Resend / SendGrid) or TXT (ZeptoMail)" />
            <CopyableField name="dkim-value" label="Value" value="<unique-string-from-provider-dashboard>" />
          </div>

          <div className="bg-slate-800/40 rounded-md p-4 border border-slate-700">
            <p className="text-xs uppercase text-violet-400 font-bold tracking-wider mb-2">DMARC Record (TXT)</p>
            <p className="text-xs text-slate-400 mb-3">
              DMARC is optional for getting started but strongly recommended (and required for Google/Yahoo at high volume).
              Start with a permissive policy and tighten later.
            </p>
            <CopyableField name="dmarc-host" label="Host / Name" value="_dmarc" />
            <CopyableField name="dmarc-type" label="Type" value="TXT" />
            <CopyableField name="dmarc-value" label="Value (recommended starting policy)" value="v=DMARC1; p=none; rua=mailto:dmarc@recruitizr.ai; pct=100" />
            <p className="text-xs text-slate-500 mt-2">
              <strong>p=none</strong> means "report but don't reject yet" — gives you a 2-week observation window. After confirming no false positives in DMARC reports, upgrade to <code className="bg-slate-900 px-1 rounded">p=quarantine</code>, then <code className="bg-slate-900 px-1 rounded">p=reject</code>.
            </p>
          </div>
        </div>

        <div className="mt-4 bg-slate-800/40 border border-slate-700 rounded-md p-3 flex items-start gap-2">
          <Info className="h-4 w-4 text-violet-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-300">
            DNS propagation typically takes 5-30 minutes. {provider.name}'s dashboard will show a green "Verified" badge once records are detected. You can verify externally at:&nbsp;
            <a href="https://mxtoolbox.com/dmarc.aspx" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:underline">MXToolbox DMARC checker</a>
          </p>
        </div>
      </Section>

      {/* Step 4: API key */}
      <Section icon={Settings} title="Step 4 — Generate API key & configure Recruitizr">
        <ol className="space-y-3 text-sm text-slate-300">
          <li>
            <strong className="text-white">4.1</strong> In your {provider.name} dashboard, generate an API key with <strong>"send email"</strong> permission.
            {provider.id === 'resend' && (
              <span> Go to <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:underline">API Keys</a> → Create API Key → Permission: Full access.</span>
            )}
            {provider.id === 'sendgrid' && (
              <span> Go to <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:underline">Settings → API Keys</a> → Create → Restricted Access → enable "Mail Send".</span>
            )}
            {provider.id === 'zeptomail' && (
              <span> Go to Mail Agents → choose your agent → Setup Info → copy the <strong>Send Mail Token</strong> (looks like <code className="bg-slate-800 px-1 rounded text-xs">Zoho-enczapikey ...</code>).</span>
            )}
          </li>
          <li>
            <strong className="text-white">4.2</strong> SSH into your server (or use the platform shell) and edit <code className="bg-slate-800 px-1 rounded text-xs">/app/backend/.env</code>:
          </li>
        </ol>

        <div className="mt-3 bg-slate-950 border border-slate-700 rounded-md p-4 font-mono text-xs overflow-x-auto">
          <pre className="text-slate-300 whitespace-pre">{`# /app/backend/.env

EMAIL_PROVIDER=${provider.id}
EMAIL_API_KEY=<paste your ${provider.name} API key here>
EMAIL_FROM_ADDRESS=noreply@recruitizr.ai
EMAIL_FROM_NAME=Recruitizr
FRONTEND_URL=https://recruitizr.ai${provider.id === 'zeptomail' ? '\nZEPTOMAIL_HOST=api.zeptomail.com  # use api.zeptomail.in for India accounts' : ''}`}</pre>
        </div>

        <ol start={3} className="space-y-3 text-sm text-slate-300 mt-3">
          <li>
            <strong className="text-white">4.3</strong> Restart the backend so the new env vars are picked up:
            <code className="block mt-1 bg-slate-950 border border-slate-700 rounded-md p-2 font-mono text-xs text-violet-300">sudo supervisorctl restart backend</code>
          </li>
          <li>
            <strong className="text-white">4.4</strong> Go back to the&nbsp;
            <button onClick={() => navigate('/super-admin/email')} className="text-violet-300 hover:underline" data-testid="email-page-link">Email Notifications page</button>
            &nbsp;→ enter your own email → click <strong>"Send Test"</strong> → confirm a real email arrives in your inbox.
          </li>
        </ol>
      </Section>

      {/* Step 5: Production deploy */}
      <Section icon={CheckCircle2} title="Step 5 — Deploy & verify in production" defaultOpen={false}>
        <ol className="space-y-3 text-sm text-slate-300">
          <li>
            <strong className="text-white">5.1</strong> Redeploy from the Emergent platform so the production server reads the new env vars.
            <span className="block text-xs text-slate-500 mt-1">
              Verify the live build using the&nbsp;
              <button onClick={() => navigate('/super-admin/version')} className="text-violet-300 hover:underline">Version tool</button>
              &nbsp;— Git SHA should match preview.
            </span>
          </li>
          <li>
            <strong className="text-white">5.2</strong> Trigger one real flow (e.g., create a new user from the tenant admin panel) and check the recipient's inbox.
          </li>
          <li>
            <strong className="text-white">5.3</strong> Open the&nbsp;
            <button onClick={() => navigate('/super-admin/email')} className="text-violet-300 hover:underline">Email Notifications</button>
            &nbsp;page → "Recent Notifications" → confirm the status badge shows <strong>SENT</strong> (not MOCK or FAILED).
          </li>
          <li>
            <strong className="text-white">5.4</strong> (Optional) After 2 weeks of clean DMARC reports, tighten the DMARC policy from <code className="bg-slate-800 px-1 rounded">p=none</code> to <code className="bg-slate-800 px-1 rounded">p=quarantine</code>, then eventually <code className="bg-slate-800 px-1 rounded">p=reject</code>.
          </li>
        </ol>
      </Section>

      {/* Troubleshooting */}
      <Section icon={FileText} title="Troubleshooting" defaultOpen={false}>
        <div className="space-y-3 text-sm text-slate-300">
          <div>
            <p className="font-semibold text-white">Test email shows status FAILED</p>
            <p className="text-xs text-slate-400 mt-1">
              Check the error string in the Recent Notifications table. Common causes:
              (a) sender domain not yet verified in {provider.name} dashboard,
              (b) API key was revoked,
              (c) FROM address doesn't match the verified domain,
              (d) {provider.name} account is in sandbox mode (verify a payment method).
            </p>
          </div>
          <div>
            <p className="font-semibold text-white">Emails land in spam folder</p>
            <p className="text-xs text-slate-400 mt-1">
              DKIM not yet propagated, or SPF still missing. Check at&nbsp;
              <a href="https://www.mail-tester.com/" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:underline">mail-tester.com</a>
              &nbsp;— aim for a score of 10/10.
            </p>
          </div>
          <div>
            <p className="font-semibold text-white">"Live delivery active" but emails not arriving</p>
            <p className="text-xs text-slate-400 mt-1">
              The {provider.name} dashboard usually has a delivery log per email. Check it for bounces, drops, or spam reports.
              Most providers throttle new senders for the first 1-3 days — gradually ramp volume rather than blasting 10,000 emails on day 1.
            </p>
          </div>
          <div>
            <p className="font-semibold text-white">Switching providers later</p>
            <p className="text-xs text-slate-400 mt-1">
              Edit <code className="bg-slate-800 px-1 rounded text-xs">.env</code> → change <code className="bg-slate-800 px-1 rounded text-xs">EMAIL_PROVIDER</code> and <code className="bg-slate-800 px-1 rounded text-xs">EMAIL_API_KEY</code> → restart backend. No code changes needed.
            </p>
          </div>
        </div>
      </Section>

      <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 text-center">
        <p className="text-sm text-slate-300">
          Ready to test? <button onClick={() => navigate('/super-admin/email')} className="text-violet-300 font-semibold hover:underline" data-testid="goto-email-btn">Go to Email Notifications →</button>
        </p>
      </div>
    </div>
  );
}
